/**
 * Group already-split QZ HTML pages so consecutive sheets for the same
 * printer/tray/size share one qz.print(config, payloads[]) call.
 *
 * Page split remains mandatory: QZ HTML pixel print with scaleContent:true
 * ignores CSS page breaks, so each payload is still one physical sheet.
 * Do not group A4 with GoDEX (different printer/size).
 */

export const QZ_MAX_HTML_PAGES_PER_SPOOL = 8;
export const QZ_SAME_PRINTER_HTML_SETTLE_MS = 150;
export const QZ_PRINTER_CHANGE_SPOOL_GAP_MS = 50;
export const QZ_HTML_SPOOL_TIMEOUT_BASE_MS = 30000;
export const QZ_HTML_SPOOL_TIMEOUT_PER_PAGE_MS = 10000;
export const QZ_HTML_SPOOL_TIMEOUT_MAX_MS = 180000;

export function qzHtmlSpoolFingerprint(printerName, configOptions = {}) {
  const size = configOptions.size || {};
  return [
    String(printerName || ''),
    String(configOptions.printerTray ?? ''),
    Number(size.width) || 0,
    Number(size.height) || 0,
    String(configOptions.orientation || ''),
    Number(configOptions.rotation) || 0,
    String(configOptions.density ?? ''),
    String(configOptions.duplex || ''),
    Number(configOptions.margins) || 0,
    String(configOptions.colorType || ''),
  ].join('\u0001');
}

export function groupConsecutiveQzHtmlSpools(
  pages = [],
  { maxPages = QZ_MAX_HTML_PAGES_PER_SPOOL } = {},
) {
  const cap = Math.max(1, Math.min(50, Number(maxPages) || QZ_MAX_HTML_PAGES_PER_SPOOL));
  const groups = [];
  (pages || []).forEach((page) => {
    const last = groups[groups.length - 1];
    const sameRoute =
      last &&
      last.fingerprint &&
      last.fingerprint === page.fingerprint &&
      last.payloads.length < cap;
    if (sameRoute) {
      last.payloads.push(page.payload);
      last.results.push(page.result);
      last.jobs.push(page.job);
      return;
    }
    groups.push({
      fingerprint: page.fingerprint,
      printer: page.printer,
      config: page.config,
      payloads: [page.payload],
      results: [page.result],
      jobs: [page.job],
    });
  });
  return groups;
}

export function qzHtmlSpoolTimeoutMs(pageCount) {
  const pages = Math.max(1, Number(pageCount) || 1);
  return Math.min(
    QZ_HTML_SPOOL_TIMEOUT_MAX_MS,
    QZ_HTML_SPOOL_TIMEOUT_BASE_MS + QZ_HTML_SPOOL_TIMEOUT_PER_PAGE_MS * pages,
  );
}

export function qzInterSpoolGapMs(previousPrinter, nextPrinter) {
  const previous = String(previousPrinter || '');
  const next = String(nextPrinter || '');
  if (!previous || !next || previous === next) return 0;
  return QZ_PRINTER_CHANGE_SPOOL_GAP_MS;
}

export function inferQueuedPayloadType(item) {
  if (item?.kind === 'carrier-pdf' || item?.kind === 'general-pdf') return 'pdf';
  return 'html';
}

/**
 * Queue settle after QZ accepts a spool. Skip (0) when there is no next item.
 * Same printer + tray + HTML→HTML uses a short gap instead of the full
 * operator delay (default 1200 ms), which was aimed at printer changes and PDFs.
 */
export function resolveQzSettleDelayMs({
  configuredMs,
  hasNext = false,
  previousPrinter,
  nextPrinter,
  previousTray,
  nextTray,
  previousPayloadType,
  nextPayloadType,
} = {}) {
  if (!hasNext) return 0;
  const configured = Math.max(0, Math.min(10000, Number(configuredMs) || 0));
  const samePrinter =
    String(previousPrinter || '') !== '' &&
    String(previousPrinter) === String(nextPrinter || '');
  const sameTray = String(previousTray || '') === String(nextTray || '');
  const bothHtml = previousPayloadType !== 'pdf' && nextPayloadType !== 'pdf';
  if (samePrinter && sameTray && bothHtml) {
    return Math.min(configured, QZ_SAME_PRINTER_HTML_SETTLE_MS);
  }
  return configured;
}

export default groupConsecutiveQzHtmlSpools;
